import dayjs from "dayjs";
import React from "react";
import {
  ChartComponentProps,
  ChartConfig,
  createChartConfig,
  TimeSeriesDataPoint,
} from "src/components/Referral-partner/Analytics/analyticsTypes";
import ReferralFees from "src/components/Referral-partner/Analytics/charts/ReferralFees";
import { WarningWrapper } from "src/components/Referral-partner/Analytics/styles/shared.styles";
import api from "src/store/features/api";
import { useRPAnalyticsState } from "src/store/features/RPAnalytics.slice";

const useGetReferralFessConfig = (): { referralfees: ChartConfig<TimeSeriesDataPoint> } => {
  const { chartDateRange } = useRPAnalyticsState();

  // fetch data and states
  const {
    data: rawFees,
    isLoading: feesIsLoading,
    isFetching: feesIsFetching,
    isError: feesIsError,
    error: feesError,
  } = api.useGetReferralPartnerFeesQuery({
    timeFrame: chartDateRange.referralfees,
    timezone: dayjs.tz.guess(),
  });

  // format data
  const totalFeesEarned = React.useMemo((): string => {
    if (!rawFees) return "-";
    return parseFloat(rawFees.data.totalFees.toFixed(2)).toLocaleString();
  }, [rawFees]);

  // format data
  const feesData = React.useMemo((): TimeSeriesDataPoint[] => {
    if (!rawFees) return [];
    return rawFees.data.timeframeData
      .map(
        (fee): TimeSeriesDataPoint => ({
          timestamp: fee.timestamp,
          fees: fee.fees,
        }),
      )
      .filter((entry) => (entry.fees as number) > 0);
  }, [rawFees]);

  // create chart config
  const referralFeesConfig = React.useMemo(
    (): { referralfees: ChartConfig<TimeSeriesDataPoint> } => ({
      referralfees: createChartConfig<TimeSeriesDataPoint>({
        title: "Fees earned over time",
        type: "area",
        menuMode: "normal",
        styles: { padding: "12px" },
        defaultDate: "all time",
        dateOptions: [
          {
            label: "month",
            value: "1M",
          },
          {
            label: "1 year",
            value: "1Y",
          },
          {
            label: "all time",
            value: "ALL",
          },
        ],
        mainChart: true,
        isLoading: feesIsLoading || feesIsFetching,
        data: feesData,
        component: ({ data, title }: ChartComponentProps<TimeSeriesDataPoint>): JSX.Element => {
          if (feesIsError) {
            return <WarningWrapper>{feesError?.message}</WarningWrapper>;
          } else if (data.length <= 0) {
            return <WarningWrapper color="#002035">No Data</WarningWrapper>;
          } else {
            return <ReferralFees title={title} data={data} totalFees={totalFeesEarned} />;
          }
        },
      }),
    }),
    [feesData, feesIsLoading, feesIsFetching, feesIsError, feesError],
  );

  return referralFeesConfig;
};

export default useGetReferralFessConfig;
